// Scheduled / pre-orders.
//
// All slots are in KUWAIT wall-clock time (the restaurant's local time), so a
// customer browsing from another timezone still schedules against Kuwait hours.
// Kuwait is UTC+3 year-round (no DST), which lets us convert a Kuwait wall-clock
// time to an epoch with a fixed offset.

const KUWAIT_TZ = "Asia/Kuwait";
const SERVICE_START_HOUR = 11; // first slot 11:00
const SERVICE_LAST_HOUR = 22; // last slot 22:30
const SLOT_MINUTES = 30;
const LEAD_MINUTES = 45; // earliest a "today" slot may be, from now
const DAYS_AHEAD = 4; // today + next 4 days

export interface DayOption {
  /** Kuwait calendar date key YYYY-MM-DD (stable id). */
  key: string;
  y: number;
  mo: number; // 1-12
  d: number;
  /** 0 = today, 1 = tomorrow, … */
  offset: number;
}

export interface SlotOption {
  /** Epoch ms for the slot start. */
  ts: number;
  /** e.g. "11:30" (Kuwait 24h). */
  label: string;
}

interface KWParts {
  y: number;
  mo: number;
  d: number;
  h: number;
  mi: number;
}

function kuwaitParts(epoch: number): KWParts {
  const f = new Intl.DateTimeFormat("en-GB", {
    timeZone: KUWAIT_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const p = Object.fromEntries(f.formatToParts(new Date(epoch)).map((x) => [x.type, x.value]));
  // Intl may render midnight as "24" in some engines — normalise to 0.
  const h = Number(p.hour) % 24;
  return { y: Number(p.year), mo: Number(p.month), d: Number(p.day), h, mi: Number(p.minute) };
}

/** Kuwait wall-clock → epoch ms (Kuwait = UTC+3, fixed). */
function kuwaitEpoch(y: number, mo: number, d: number, h: number, mi: number): number {
  return Date.UTC(y, mo - 1, d, h - 3, mi, 0);
}

/** Selectable days (today … +DAYS_AHEAD) in Kuwait. */
export function scheduleDays(now = Date.now()): DayOption[] {
  const t = kuwaitParts(now);
  const out: DayOption[] = [];
  for (let i = 0; i <= DAYS_AHEAD; i++) {
    // Normalise via a noon anchor so month/year rollover is handled.
    const anchor = kuwaitParts(kuwaitEpoch(t.y, t.mo, t.d + i, 12, 0));
    const key = `${anchor.y}-${String(anchor.mo).padStart(2, "0")}-${String(anchor.d).padStart(2, "0")}`;
    out.push({ key, y: anchor.y, mo: anchor.mo, d: anchor.d, offset: i });
  }
  return out;
}

/** Valid time slots for a given day. Empty if the day is fully past. */
export function slotsForDay(day: DayOption, now = Date.now()): SlotOption[] {
  const earliest = now + LEAD_MINUTES * 60 * 1000;
  const out: SlotOption[] = [];
  for (let h = SERVICE_START_HOUR; h <= SERVICE_LAST_HOUR; h++) {
    for (let mi = 0; mi < 60; mi += SLOT_MINUTES) {
      const ts = kuwaitEpoch(day.y, day.mo, day.d, h, mi);
      if (ts < earliest) continue; // too soon (today) / already past
      out.push({ ts, label: `${String(h).padStart(2, "0")}:${String(mi).padStart(2, "0")}` });
    }
  }
  return out;
}

/** Human label for a scheduled timestamp, in Kuwait time. */
export function formatSchedule(ts: number, lang: "en" | "ar"): string {
  return new Intl.DateTimeFormat(lang === "ar" ? "ar-KW" : "en-GB", {
    timeZone: KUWAIT_TZ,
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(ts));
}

/** Short day label (Today / Tomorrow / weekday) for the day picker. */
export function dayLabel(day: DayOption, lang: "en" | "ar"): string {
  if (day.offset === 0) return lang === "ar" ? "اليوم" : "Today";
  if (day.offset === 1) return lang === "ar" ? "غداً" : "Tomorrow";
  return new Intl.DateTimeFormat(lang === "ar" ? "ar-KW" : "en-GB", {
    timeZone: KUWAIT_TZ,
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(new Date(kuwaitEpoch(day.y, day.mo, day.d, 12, 0)));
}
